import { AmenitiesAndSurroundings } from '@models/AmenitiesAndSurroundings';
import { Amenity, AmenityLabel } from '@src/constants';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { AmenityGroup } from '../estimator';
import './amenities-input.scss';
import { LabelAbove } from './label-above';
import { WireIcon } from './wire-icon';

/** Generate amenity summary */
const summarize = (amenities: AmenitiesAndSurroundings): string => {
    let total = 0;
    for (const key in amenities) {
        if (amenities[key as keyof AmenitiesAndSurroundings]) total++;
    }
    return `${total} Selected`;
};

export const AmenitiesInput: FunctionComponent<{
    label: string;
    onChange: (amenities: AmenitiesAndSurroundings) => void;
    value: AmenitiesAndSurroundings;
}> = ({ onChange, label, value }) => {
    const [amenities, setAmenities] = useState(value);
    const [summary, setSummary] = useState(summarize(amenities));
    const [visible, setVisible] = useState(false);
    const ref = React.createRef<HTMLDivElement>();
    let div: HTMLDivElement | null;

    useEffect(() => {
        document.addEventListener('mousedown', onDocumentClick);
        div = ref.current;
    }, []);

    useEffect(() => {
        setSummary(summarize(amenities));
        onChange(amenities);
    }, [amenities]);

    const toggleVisible = () => setVisible(!visible);

    const onDocumentClick = (e: MouseEvent) => {
        if (div && e.target && div.contains(e.target as Node)) {
            // ignore click on self
            return;
        }
        //document.removeEventListener('mousedown', onDocumentClick);
        setVisible(false);
    };

    const toggleAmenity = (key: string) => {
        setAmenities({
            ...amenities,
            [key]: !amenities[key as keyof AmenitiesAndSurroundings],
        });
    };

    return (
        <LabelAbove
            className="estimator-amenities-input"
            label={label}
            onClick={toggleVisible}
            ref={ref}
        >
            <main>
                <div className="estimator-input amenity-select" onClick={toggleVisible}>
                    <span>{summary}</span>
                    <WireIcon
                        svg={visible ? 'chevron-up' : 'chevron-down'}
                        className="btn btn-v-utility"
                        tabIndex={0}
                        onClick={toggleVisible}
                    />
                </div>
                <div className={'amenity-dropdown' + (visible ? '' : ' d-none')}>
                    {Object.keys(Amenity.groups)
                        .map((name) => Amenity.groups[name])
                        .filter((g) => g.keys.length > 0)
                        .map((g, i) => (
                            <AmenityOptionGroup
                                key={g.label}
                                amenities={amenities}
                                group={g}
                                index={i}
                                onClick={toggleAmenity}
                            />
                        ))}
                </div>
            </main>
        </LabelAbove>
    );
};

/** Group of amenity options */
export const AmenityOptionGroup: FunctionComponent<{
    group: AmenityGroup;
    amenities: AmenitiesAndSurroundings;
    index: number;
    disabled?: boolean;
    onClick: (key: string) => void;
}> = ({ group, index, onClick, amenities, disabled = false }) => {
    /** Tuple of amenity key and label sorted by label */
    const keys: [string, string][] = group.keys
        .map((k) => [k, AmenityLabel[k as keyof typeof AmenityLabel]] as [string, string])
        .sort((a, b) => a[1].localeCompare(b[1]));

    return (
        <div key={index} className="amenity-group">
            <label>{group.label}</label>
            {keys.map(([key, label], i) => (
                <AmenityOption
                    key={key}
                    index={i}
                    disabled={disabled}
                    checked={amenities[key as keyof AmenitiesAndSurroundings]}
                    onClick={onClick}
                    amenityKey={key}
                    label={label}
                />
            ))}
        </div>
    );
};

/** Single amenity checkbox */
const AmenityOption: FunctionComponent<{
    index: number;
    amenityKey: string;
    label: string;
    checked: boolean;
    disabled?: boolean;
    onClick: (key: string) => void;
}> = ({ amenityKey, label, index, checked, onClick, disabled = false }) => (
    <div className="amenity-option" key={index}>
        <input
            id={`amenity_${amenityKey}`}
            type="checkbox"
            disabled={disabled}
            name={amenityKey}
            checked={checked}
            onChange={() => onClick(amenityKey)}
            tabIndex={0}
        />
        <label htmlFor={`amenity_${amenityKey}`}>{label}</label>
    </div>
);
